/* eslint-disable @typescript-eslint/require-await */
/** @module Webinar */

import { Subject, Subscription, debounceTime, filter } from "rxjs";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { RBEvent } from 'rainbow-web-sdk/models/event.model';
import { Bubble, BubbleUserStatus } from 'rainbow-web-sdk/models/bubble.model';
import { JingleSession } from "rainbow-web-sdk/libs/strophe/strophe.jingle.session";
import { isAfter, subMinutes } from "date-fns";
import { MainService, WEBINAR_SVC, WebinarService, XMPP_SVC } from "rainbow-web-sdk/services";
import { CONTACT_SVC, ContactService } from "../services/core/contact.service";
import { ConversationRB } from "./conversation.model";
import { $msg } from "strophe.js";

/**
 * Webinar status
 */
export enum WebinarStatus {
    UPCOMING = 'upcoming',
    WAITING_ROOM = 'waitingRoom',
    PRACTICE_ROOM = 'practiceRoom',
    PRACTICE_WAITING_ROOM = 'practiceWaitingRoom',
    IN_PROGRESS = 'inProgress',
    TERMINATED = 'terminated'
}

export enum WebinarSessionParticipantMedia {
    AUDIO = 'audio',
    VIDEO = 'video',
    SHARING = 'sharing'
}

export enum WebinarMediaType {
    participant = 'participant',
    sharing = 'sharing',
    mix = 'mix'
}

export interface WebinarCodecs {
    audio?: string;
    video?: string;
    sharing?: string;
}

export enum WebinarSessionParticipantEvents {
    UPDATE_PARTICIPANT = 'updateParticipant',
    UPDATE_MEDIA = 'updateMedia',
    UPDATE_ORIGINAL_STREAM = 'updateOriginalStream',
    MUTE_PARTICIPANT = 'muteParticipant',
    ACTIVE_SPEAKER = 'activeSpeaker'
}

export interface WebinarSessionParticipantMedias {
    audio: boolean;
    video: boolean;
    sharing: boolean;
}

export interface WebinarSessionParticipantAvailableStreams {
    audioVideo: boolean;
    sharing: boolean;
}

export enum WebinarSessionParticipantType {
    USER = 'USER',
    MCUBOT = 'MCUBOT'
}
export class WebinarSessionParticipant {
    public id: string;
    public type: WebinarSessionParticipantType = WebinarSessionParticipantType.USER;
    public contact: Contact = null;
    public activeSpeaker: boolean = false;
    public mute: boolean = true;
    public muteSFU: boolean = true;
    public muteMCU: boolean = true;
    public onStage: boolean = false;
    public sharingOnStage: boolean = false;
    public trackedBackstage: boolean = false;
    public searchString: string = null;
    public role: string = null;
    public raiseHand: boolean = false;
    public raiseHandDate?: Date;
    public raiseHandPosition?: number;
    public allowedToTalk: boolean = false;
    public invitedToGoOnStage?: any;
    public audioConfig: string;
    public audible: boolean = true;

    public audioVideoSessionId?: string;
    public audioVideoSession: JingleSession = null;
    public originalAudioVideoStream: MediaStream = null;
    public audioVideoStream?: MediaStream;
    public mcuAudioSessionId?: string;
    public mcuAudioStream?: MediaStream;
    public sharingSessionId?: string;
    public sharingSession: JingleSession = null;
    public sharingStream?: MediaStream;

    public medias: WebinarSessionParticipantMedias = { audio: false, video: false, sharing: false };
    public availableStreams: WebinarSessionParticipantAvailableStreams = { audioVideo: false, sharing: false };

    public rxSubject: Subject<any> = new Subject<any>();
    public subStreamLevel: number = -1;
    public temporalLevel: number = -1;

    public nameAndEmail?: string;
    public company?: string;

    private contactService?: ContactService;

    public static create(id: string) { return new WebinarSessionParticipant(id); }

    constructor(id: string) {
        this.id = id;
        this.contactService = MainService.getInstance().inject<ContactService>(CONTACT_SVC);
    }

    public sendEvent(event: WebinarSessionParticipantEvents, data?: any): void {
        this.rxSubject.next(RBEvent.create(event, data));
    }

    /**
     * Subscribe to events on the WebinarSessionParticipant (all events are of RBEvent type);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - a list of event names that the user wants to receive when subscribing (used a filter)
     */
    public subscribe(handler: (event: RBEvent<WebinarSessionParticipantEvents>) => any, eventNames?: WebinarSessionParticipantEvents | WebinarSessionParticipantEvents[], debounce: number = 0): Subscription {
        let observable = this.rxSubject.asObservable();
        if (eventNames) {
            observable = observable.pipe(filter((event: RBEvent<WebinarSessionParticipantEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }));
        }
        if (debounce > 0) observable = observable.pipe(debounceTime(debounce));
        return observable.subscribe((event: RBEvent<WebinarSessionParticipantEvents>) => {
            try { handler(event); } catch { /**/ }
        });
    }

    public getSessionType(sessionId: string): string | null {
        if (this.audioVideoSession && this.audioVideoSession.sid === sessionId) return 'audio+video';
        if (this.sharingSession && this.sharingSession.sid === sessionId) return 'sharing';
        return null;
    }

    public onHold(media: 'video' | 'sharing'): boolean {
        const session = this[media === 'video' ? 'audioVideoSession' : 'sharingSession'];
        if (session) return session.onHold;
        return false;
    }

    public isLocalParticipant(): boolean {
        return this.id === this.contactService.userContact.dbId;
    }

    public setMedia(media: WebinarSessionParticipantMedia, value: boolean, clean: boolean = false): void {
        this.medias[media] = value;
        if (!value && clean) {
            switch (media) {
                case WebinarSessionParticipantMedia.AUDIO:
                    this.audioVideoSession = undefined;
                    this.audioVideoStream = undefined;
                    break;
                case WebinarSessionParticipantMedia.SHARING:
                    this.sharingSession = undefined;
                    this.sharingStream = undefined;
                    break;
                default: break;
            }
        }
    }

    public toString(): string {
        return JSON.stringify({ id: this.id, type: this.type });
    }
}

export class WebinarSessionService {
    public sid?: string;
    public audioVideoSession?: JingleSession;
    public audioVideoStream?: MediaStream;
    public static create(id: string, name: string) { return new WebinarSessionService(id, name); }
    constructor(public id: string, public type: string) { }
}

export class WebinarSession {

    public jingleJid: string;
    public activeSpeakerId: string = null;
    public localParticipant: WebinarSessionParticipant = null;
    public speakerParticipants: WebinarSessionParticipant[] = [];
    public attendeeParticipants: WebinarSessionParticipant[] = [];
    public mcuAudioSession?: any;
    public raiseHandAttendeeParticipants: WebinarSessionParticipant[] = [];
    public raiseHandSpeakers: WebinarSessionParticipant[] = [];
    public service?: WebinarSessionService;
    public connected: boolean = true;
    public masterMedia: string = null;
    public showMySharing: boolean = false;

    public static create() { return new WebinarSession(); }

    public getParticipantById(participantId: string): WebinarSessionParticipant {
        if (!participantId) return null;
        if (this.localParticipant && this.localParticipant.id === participantId) return this.localParticipant;

        const speakerParticipant = this.speakerParticipants.find((part: WebinarSessionParticipant) => { return part.id === participantId; });
        if (speakerParticipant) return speakerParticipant;

        const attendeeParticipant = this.attendeeParticipants.find((part: WebinarSessionParticipant) => { return part.id === participantId; });
        if (attendeeParticipant) return attendeeParticipant;

        return null;
    }

    public getParticipantBySession(session: JingleSession): WebinarSessionParticipant | undefined {
        if (!session) return undefined;
        let sessionIdParam = 'audioVideoSessionId';
        switch (session.localType) {
            case 'audio':sessionIdParam = 'mcuAudioSessionId'; break;
            case 'sharing':sessionIdParam = 'sharingSessionId'; break;
            default : sessionIdParam = 'audioVideoSessionId'; break;
        }
        if (this.localParticipant && this.localParticipant[sessionIdParam] === session.sid) return this.localParticipant;

        const speakerParticipant = this.speakerParticipants.find((part: WebinarSessionParticipant) => { return part[sessionIdParam] === session.sid; });
        if (speakerParticipant) return speakerParticipant;

        const attendeeParticipant = this.attendeeParticipants.find((part: WebinarSessionParticipant) => { return part[sessionIdParam] === session.sid; });
        if (attendeeParticipant) return attendeeParticipant;
        return undefined;
    }

    public getSpeakerBySessionId(sessionId: string): WebinarSessionParticipant {
        return this.speakerParticipants.find((speaker: WebinarSessionParticipant) => {
            return speaker.getSessionType(sessionId) !== null;
        });
    }

    public addOrUpdateParticipant(participant: WebinarSessionParticipant, action?: string) {
        if (this.localParticipant && this.localParticipant.id === participant.id) {
            this.localParticipant.mute = participant.mute;
            this.localParticipant.role = participant.role;
            this.localParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT);
            return;
        }

        const participants = participant.role === 'attendee' ? this.attendeeParticipants : this.speakerParticipants;
        const participantIndex = participants.findIndex((part: WebinarSessionParticipant) => { return part.id === participant.id; });
        if (participantIndex !== -1) {
            const existingParticipant = participants[participantIndex];
            existingParticipant.mute = participant.mute;
            existingParticipant.sendEvent(WebinarSessionParticipantEvents.UPDATE_PARTICIPANT);

        }
        else if (!action || action !== 'UpdateParticipant') { participants.push(participant); }
    }

    public removeParticipant(participantId: string) {
        const participantIndex = this.speakerParticipants.findIndex((part: WebinarSessionParticipant) => { return part.id === participantId; });
        if (participantIndex !== -1) { this.speakerParticipants.splice(participantIndex, 1); }
        if (this.localParticipant && this.localParticipant.id === participantId) { this.localParticipant = null; }
    }

    public addService(serviceId: string, serviceName: string): WebinarSessionService {
        this.service = WebinarSessionService.create(serviceId, serviceName);
        return this.service;
    }
}

export class WebinarParticipant {
    public email: string;
    public lastName: string;
    public firstName: string;
    public company: string;
    public jobTitle: string;
    public state: string;
    public userId: string;
    public lastAvatarUpdateDate: string;
    public country: string;

    public static create(): WebinarParticipant { return new WebinarParticipant(); }
    public static createFromData(data: any) {
        const participant = WebinarParticipant.create();
        if (data.email) participant.email = data.email;
        if (data.lastName) participant.lastName = data.lastName;
        if (data.firstName) participant.firstName = data.firstName;
        if (data.company) participant.company = data.company;
        if (data.jobTitle) participant.jobTitle = data.jobTitle;
        if (data.state) participant.state = data.state;
        if (data.userId) participant.userId = data.userId;
        if (data.lastAvatarUpdateDate) participant.lastAvatarUpdateDate = data.lastAvatarUpdateDate;
        if (data.country) participant.country = data.country;
        return participant;
    }

    public getData(): string {
        return JSON.stringify({
            email: this.email,
            userId: this.userId,
            lastName: this.lastName,
            firstName: this.firstName,
            company: this.company,
            jobTitle: this.jobTitle
        });
    }
}
export class WebinarCSVStatus {
    public errorDetails: string;
    public okCount: number = 0;
    public errorCount: number = 0;
    public errorReport: any = [];

    public static create(): WebinarCSVStatus { return new WebinarCSVStatus(); }
}

export class WebinarCloneParams {
    /**
     * Cloned webinar name
     * If not specified, the former name will be used
     */
    name?: string;

    /**
     * Cloned webinar subject
     * If not specified, the former subject will be used
     */
    subject?: string;

    /** clone webinar start date */
    startDate?: Date;

    /** clone webinar end date */
    endDate?: Date;

    /** clone webinar timeZone */
    timeZone?: string;

    /** clone or not the speakers even if some of them declined the previous invitation */
    cloneSpeakers: boolean = true;

    /** param cloneAttendees - clone or not the attendees */
    cloneAttendees: boolean = false;

    /** clone webinar waiting room start date */
    waitingRoomStartDate?: string;

    /** licence profile identifier */
    profileId: string;
}

/**
 * @eventProperty
 * `Webinar` specific events
 */
export enum WebinarEvents {

    /**
     * @eventProperty
     * This RBEvent is send when the webinar status change
     */
    ON_STATUS_CHANGE = 'ON_STATUS_CHANGE',

    /**
     * @eventProperty
     * This RBEvent is send when
     */
    ON_DATA_UPDATE = 'ON_DATA_UPDATED',

    /**
     * @eventProperty
     * This RBEvent is send when
     */
    ON_AVATAR_UPDATE = 'ON_AVATAR_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is send when
     */
    ON_LOGO_UPDATE = 'ON_LOGO_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is send when
     */
    ON_CHAT_OPTIONS_UPDATE = 'ON_CHAT_OPTIONS_UPDATE',

    /**
     * @eventProperty
     * This RBEvent is send when the number of raiseHand change
     */
    ON_RAISEHAND_CHANGE = 'ON_RAISEHAND_CHANGE'
}

/**
 * Represents a webinar with various properties and methods for managing its lifecycle,
 * participants, media, and other related functionalities.
 */
export interface Webinar {
    id: string;
    name: string;
    subject: string;
    webinarStartDate: string;
    webinarEndDate: string;
    webinarDuration: string;
    webinarLastDateCheck: string;
    waitingRoomStartDate: string;
    timeZone: string;
    roomId: string;
    roomModeratorsChatId: string;
    room: Bubble;
    practiceRoom: Bubble;
    organizers: any[];
    speakers: any[];
    participants: WebinarParticipant[];
    profileId: string;
    pendingParticipantNumber: number;
    acceptedParticipantNumber: number;
    rejectedParticipantNumber: number;
    color: string;
    creatorId: string;
    creatorCompanyId: string;
    isCreator: boolean;
    isOrganizer: boolean;
    isSpeaker: boolean;
    isAttendee: boolean;
    isGuestAttendee: boolean;
    isGuest: boolean;
    isMCUBOT: boolean;
    password: string;
    approvalRegistrationMethod: string;
    registrationUuid: string;
    published: boolean;
    moderatorsNotified: boolean;
    status: WebinarStatus;
    webinarUrl: string;
    rxSubject: Subject<any>;
    rxParticipantsSubject: Subject<any>;
    rxServiceSubject: Subject<any>;
    rxInfoSubject: Subject<any>;
    isWebinarSync: boolean;
    session: WebinarSession | null;
    waitingRoomMultimediaURLs: string[];
    stageBackground: string;
    reminderDates: string[];
    subtitlesEnable: boolean;
    blur: boolean;
    bgReplaceUrl: string;
    chatOption: string;
    chatOptionExpired: boolean;
    chatOptionTimer: any;
    recorded?: string;
    hideRecordInfo: boolean;
    recordFileId: string;
    recordStatus: string;
    action: string;
    lastAvatarUpdate?: string;
    lastActorsInfo?: any;
    incomingStreamsDisabled: any;

    sendEvent(name: WebinarEvents, data?: any): void;
    subscribe(handler: any): Subscription;
    subscribe2(handler: (event: RBEvent<WebinarEvents>) => any, eventNames?: WebinarEvents | WebinarEvents[]): Subscription;
    updateAttendeeCounters(): void;
    getData(): string;
    updateRaiseHandParticipants(): void;
    isTerminated(): boolean;
    isWaitingRoomStarted(): boolean;
    isInvitation(): boolean;

    /**
     * Asynchronous method to clone a webinar
     * @param params - The webinar parameters to clone
     */
    clone(params: WebinarCloneParams): Promise<Webinar>;

    /**
     * Asynchronous method to update webinar avatar on server
     * @param avatarImg - base64 encoded webinar avatar
     */
    updateAvatar(avatarImg?: string): Promise<Webinar>;

    /**
    * Asynchronous method to update webinar logo on server
    * @param logoSrc - base64 encoded webinar logo
    */
    updateLogo(logoSrc?: string): Promise<Webinar>;

    /**
     * Allow a rainbow attendee to join the webinar room
     */
    joinAsRainbowAttendee(): Promise<void>;

    /**
     * Allow a rainbow user to join the webinar
     */
    join(): Promise<void>;

    /**
     * Allow to leave a webinar
     *  - remove the user from the stage (if it was on the stage)
     *  - remove the user from the conference (stop the session if last user)
     * @param forced - when set to true this method stop the session (even if not the last user connect in conf)
     * @param nowait - when set to true this method call async child methods without waiting the end of execution (used in last chance deconnection event mechanism (onunload event))
     * @returns A promise that is resolved when the user has join the conference
     */
    leave(forced?: boolean, nowait?: boolean, reason?: string): Promise<void>;

    /**
     * Update existing webinar with server data
     * @param webinarData - server webinar data structure
     */
    updateFromData(webinarData: any): void;

    /**
     * Accept webinar invitation (as a speaker)
     */
    acceptInvitation(): Promise<void>;

    /**
     * Add audio/video media
     * @param media - the media to add
     */
    addAudioVideoMedia(media: string, stream?: MediaStream, replaceBg?: boolean): Promise<void>;

    /**
     * Add a sharing media
     * @param stream - the mediaStream to add as sharing media
     */
    addSharingMedia(stream: MediaStream): Promise<void>;

    /**
     * Put local participant audio/video media on stage
     * @param master - when set to true the participant media is presented on master
     */
    putLocalParticipantAudioVideoMediaOnStage(master?: boolean): Promise<Webinar>;

    /** @internal */
    putLocalParticipantMixMediaOnStage(): Promise<Webinar>;

    /**
     * Put a participant sharing media on stage
     * @param master - when set to true the participant sharing is presented on master
     */
    putParticipantSharingMediaOnStage(participant: WebinarSessionParticipant, master?: boolean): Promise<Webinar>;

    /**
     * Remove a participant audio/video media from the stage
     * @param participant - the participant to remove from the stage
     */
    removeParticipantAudioVideoMediaFromStage(participant: WebinarSessionParticipant): Promise<Webinar>;

    /**
     * Remove a participant sharing media from the stage
     * @param participant - the participant to remove from the stage
     */
    removeParticipantSharingMediaFromStage(participant: WebinarSessionParticipant): Promise<Webinar>;

    /**
     * Share the local participant audioConfig
     * @param audioConfig - the audioConfig "stage"|"backstage"|"stageAndBackstage"|a userId
     */
    shareLocalParticipantAudioConfig(audioConfig: string): Promise<Webinar>;

    /**
     * Subscribe to participant media
     * @param participant - the publisher participant
     * @param media - the media
     */
    subscribeToPublisher(participant: WebinarSessionParticipant, media: string): Promise<Webinar>;

    /**
     * Unsubscribe to participant media
     * @param participant - the publisher participant
     * @param media - the media
     */
    unsubscribeToPublisher(participant: WebinarSessionParticipant, media: string, disableStream?: boolean): Promise<Webinar>;

    /**
     * Send an event to publisher for asking to go on stage
     * @param participant - the speaker participant
     * @param media - the media requested to stage
     */
    askForStaging(participant: WebinarSessionParticipant, media: string): Promise<Webinar>;

    /**
     * Decline an "ask to go on stage" offer
     * @param participant - the speaker participant
     * @param media -  the media requested to stage
     */
    declineStagingInvitation(participant: WebinarSessionParticipant, media: string): Promise<Webinar>;

    muteParticipant(participant: WebinarSessionParticipant, mute?: boolean): Promise<Webinar>;
    declineUnmuteInvitation(fromParticipant: WebinarSessionParticipant): Promise<Webinar>;

    /**
     * Permit to known if it is possible to add more media audioVideo or sharing on scene
     */
    isStageAvailable(): boolean;

    allowAttendeeToTalk(userId: string): Promise<Webinar>;
    disableAttendeeToTalk(userId: string): Promise<Webinar>;
    getConnectedUserRole(): string;
    sendCountdownMessage(): Promise<void>;
    lowerHand(userId: string): Promise<Webinar>;

    /**
     * Downloads the recording of this webinar.
     * @returns A promise that resolves when the download is complete.
     */
    downloadRecord(): Promise<void>;

}
export class WebinarRB implements Webinar {

    private webinarService?: any;
    private xmppService?: any;

    id: string = null;
    name: string = '';
    public subject: string = '';
    public webinarStartDate: string = null;
    public webinarEndDate: string = null;
    public webinarDuration: string = null;
    public webinarLastDateCheck: string = null;
    public waitingRoomStartDate: string = null;
    public timeZone: string = null;
    public roomId: string = null;
    public roomModeratorsChatId: string = null;
    public room: Bubble = null;
    public practiceRoom: Bubble = null;
    public organizers: any[] = [];
    public speakers: any[] = [];
    public participants: WebinarParticipant[] = [];
    public profileId: string = null;
    public pendingParticipantNumber: number = 0;
    public acceptedParticipantNumber: number = 0;
    public rejectedParticipantNumber: number = 0;
    public color: string = '';
    public creatorId: string = null;
    public creatorCompanyId: string = null;
    public isCreator: boolean = false;
    public isOrganizer: boolean = false;
    public isSpeaker: boolean = false;
    public isAttendee: boolean = true;
    public isGuestAttendee: boolean = true;
    public isGuest: boolean = true;
    public isMCUBOT: boolean = false;
    public password: string = null;
    public approvalRegistrationMethod: string = 'automatic';
    public registrationUuid: string = null;
    public published: boolean = false;
    public moderatorsNotified: boolean = false;
    public status: WebinarStatus = WebinarStatus.UPCOMING;
    public webinarUrl: string = null;
    public rxSubject: Subject<any> = new Subject<any>();
    public rxParticipantsSubject: Subject<any> = new Subject<any>();
    public rxServiceSubject: Subject<any> = new Subject<any>();
    public rxInfoSubject: Subject<any> = new Subject<any>();
    public isWebinarSync: boolean = true;
    public session: WebinarSession | null = null;
    public waitingRoomMultimediaURLs: string[] = [];
    public stageBackground: string = 'hop';
    public reminderDates: string[] = [];
    public subtitlesEnable: boolean = false;
    public blur: boolean = false;
    public bgReplaceUrl: string = null;
    public chatOption: string = 'participant';
    public chatOptionExpired: boolean = false;
    public chatOptionTimer: any = null;
    public recorded?: string;
    public hideRecordInfo: boolean = false;
    public recordFileId: string;
    public recordStatus: string;
    public action: string = null;
    public lastAvatarUpdate?: string;
    public lastActorsInfo?: any;
    public incomingStreamsDisabled: any = { video: false, sharing: false };

    private constructor() {
        this.webinarService = MainService.getInstance().inject<WebinarService>(WEBINAR_SVC);
        this.xmppService = MainService.getInstance().inject<WebinarService>(XMPP_SVC);
    }

    public static create() { return new WebinarRB(); }

    public static createFromData(webinarData: any) {
        const webinar = new WebinarRB();
        webinar.updateFromData(webinarData);
        return webinar;
    }

    public sendEvent(name: WebinarEvents, data?: any): void {
        this.rxSubject.next(RBEvent.create<WebinarEvents, any>(name, data));
    }

    public subscribe2(handler: (event: RBEvent<WebinarEvents>) => any, eventNames?: WebinarEvents | WebinarEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<WebinarEvents>) => {
                if (event?.name && eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event?.name === eventNames;
            }))
            : this.rxSubject;
        return observable.subscribe((event: RBEvent<WebinarEvents>) => { try { handler(event); } catch { /**/ } });
    }

    /**
     * Update existing webinar with server data
     * @param webinarData - server webinar data structure
     */
    public updateFromData(webinarData: any): void {

        // Fill data
        if (webinarData.name) { this.name = webinarData.name; }
        if (webinarData.subject || webinarData.subject === '') { this.subject = webinarData.subject; }
        if (webinarData.organizers) { this.organizers = webinarData.organizers; }
        if (webinarData.speakers) { this.speakers = webinarData.speakers; }
        if (webinarData.creatorId) { this.creatorId = webinarData.creatorId; }
        if (webinarData.creatorCompanyId) { this.creatorCompanyId = webinarData.creatorCompanyId; }
        if (webinarData.password) { this.password = webinarData.password; }
        if (webinarData.approvalRegistrationMethod) { this.approvalRegistrationMethod = webinarData.approvalRegistrationMethod; }
        if (webinarData.registrationUuid) { this.registrationUuid = webinarData.registrationUuid; }
        if (webinarData.waitingRoomMultimediaURL) { this.waitingRoomMultimediaURLs = webinarData.waitingRoomMultimediaURL; }
        if (webinarData.stageBackground) { this.stageBackground = webinarData.stageBackground; }
        if (webinarData.timeZone) { this.timeZone = webinarData.timeZone; }

        if (webinarData.id) { this.id = webinarData.id; }
        if (webinarData.webinarStartDate) { this.webinarStartDate = webinarData.webinarStartDate; }
        if (webinarData.webinarEndDate) { this.webinarEndDate = webinarData.webinarEndDate; }
        if (webinarData.waitingRoomStartDate) { this.waitingRoomStartDate = webinarData.waitingRoomStartDate; }
        if (webinarData.roomId) { this.roomId = webinarData.roomId; }
        if (webinarData.roomModeratorsChatId) { this.roomModeratorsChatId = webinarData.roomModeratorsChatId; }
        if (webinarData.participants) { this.participants = webinarData.participants.map((participant: any) => WebinarParticipant.createFromData(participant)); }
        if (webinarData.emailNotification) { this.published = webinarData.emailNotification; }
        if (webinarData.moderatorsSelectedAnNotified) { this.moderatorsNotified = webinarData.moderatorsSelectedAnNotified; }
        if (webinarData.reminderDates) { this.reminderDates = webinarData.reminderDates; }
        if (webinarData.profileId) { this.profileId = webinarData.profileId; }
        if (webinarData.chatOption) { this.chatOption = webinarData.chatOption; }
        if (webinarData.roomLastAvatarUpdateDate) { this.lastAvatarUpdate = webinarData.roomLastAvatarUpdateDate; }
        if (webinarData.recording !== undefined) { this.recorded = webinarData.recording === true ? 'true' : 'false'; }
        if (webinarData.lastDateVerification) { this.webinarLastDateCheck = webinarData.lastDateVerification; }
        if (webinarData.webinarRecording) {
            this.recordFileId = webinarData.webinarRecording.fileId;
            this.recordStatus = webinarData.webinarRecording.status;
        }

        // Handle status
        if (webinarData.isActive) { this.status = WebinarStatus.IN_PROGRESS; }
        if (webinarData.state !== undefined) {
            switch (webinarData.state) {
                case 'webinar': this.status = WebinarStatus.IN_PROGRESS; break;
                case 'waitingRoom': this.status = WebinarStatus.WAITING_ROOM; break;
                case 'practiceWaitingRoom': this.status = WebinarStatus.PRACTICE_WAITING_ROOM; break;
                case 'terminated': this.status = WebinarStatus.TERMINATED; break;
                default: this.status = WebinarStatus.UPCOMING; break;
            }
        }
        if (this.published && this.status === WebinarStatus.UPCOMING) {
            const nowDate = Date.now();
            const waitingRoomStartDate = new Date(this.waitingRoomStartDate);
            if (nowDate >= waitingRoomStartDate.getTime()) this.status = WebinarStatus.WAITING_ROOM;
        }

        // Handle moderators
        this.organizers.forEach((organizer: any) => { organizer.role = 'organizer'; });
        this.speakers.forEach((speaker: any) => { speaker.role = 'speaker'; });

        if (webinarData.guestSpeakers) {
            webinarData.guestSpeakers.forEach((guestSpeaker: any) => {
                this.speakers.push({ status: guestSpeaker.status, email: guestSpeaker.email, role: 'speaker' });
            });
        }

        this.updateAttendeeCounters();

        // Compute default color
        if (this.name.length) { this.color = this.webinarService?.computeWebinarColor(this.name); }

        // Compute webinarURLwebappWebinarRegistrationUrl
        if (config.webappWebinarRegistrationUrl) this.webinarUrl = `${config.webappWebinarRegistrationUrl.replace(".red", ".com")}${this.registrationUuid}`;
        else {
            this.webinarUrl = `${config.webservices.protocol}://webinar.${config.webservices.server.replace(".red", ".com")}/${this.registrationUuid}`;
            if (window.location.origin.indexOf("localhost") !== -1) { this.webinarUrl = `localhost:8000/#/webinar/${this.registrationUuid}`; }
            if (window.location.origin.indexOf("demo") !== -1) { this.webinarUrl = `https://web-demo.openrainbow.org/#/webinar/${this.registrationUuid}`; }
        }
    }

    public updateAttendeeCounters(): void {
        this.pendingParticipantNumber = 0;
        this.acceptedParticipantNumber = 0;
        this.rejectedParticipantNumber = 0;

        this.participants.forEach((participant: any) => {
            switch (participant.state) {
                case 'pending': this.pendingParticipantNumber++; break;
                case 'approved': this.acceptedParticipantNumber++; break;
                case 'rejected': this.rejectedParticipantNumber++; break;
                default: break;
            }
        });
    }

    public subscribe(handler: any): Subscription { return this.rxSubject.subscribe(handler); }

    public getData(): string {
        const data: any = { isOrganizer: true, passwordNeeded: false };
        if (this.name && this.name !== undefined) { data.name = this.name; }
        if (this.subject !== null && this.subject !== undefined) { data.subject = this.subject; }
        if (this.webinarStartDate && this.webinarStartDate !== undefined) { data.webinarStartDate = this.webinarStartDate; }
        if (this.webinarEndDate && this.webinarEndDate !== undefined) { data.webinarEndDate = this.webinarEndDate; }
        if (this.waitingRoomStartDate && this.waitingRoomStartDate !== undefined) { data.waitingRoomStartDate = this.waitingRoomStartDate; }
        if (this.timeZone && this.timeZone !== undefined) { data.timeZone = this.timeZone; }
        if (this.approvalRegistrationMethod && this.approvalRegistrationMethod !== undefined) { data.approvalRegistrationMethod = this.approvalRegistrationMethod; }
        if (this.waitingRoomMultimediaURLs && this.waitingRoomMultimediaURLs !== undefined && this.waitingRoomMultimediaURLs.length > 0) { data.waitingRoomMultimediaURL = this.waitingRoomMultimediaURLs; }
        if (this.stageBackground && this.stageBackground !== undefined) { data.stageBackground = this.stageBackground; }
        if (this.reminderDates && this.reminderDates.length > 0) { data.reminderDates = this.reminderDates; }
        if (this.profileId && this.profileId !== undefined) { data.profileId = this.profileId; }
        if (this.chatOption && this.chatOption !== undefined) { data.chatOption = this.chatOption; }
        if (this.webinarLastDateCheck && this.webinarLastDateCheck !== undefined) { data.lastDateVerification = this.webinarLastDateCheck; }
        if (this.recorded && this.recorded !== undefined) { data.recording = this.recorded === 'true'; }
        return JSON.stringify(data);
    }

    public updateRaiseHandParticipants(): void {
        if (this.session) {
            this.session.raiseHandSpeakers = this.session.speakerParticipants.filter(participant => participant.raiseHand);
            this.session.raiseHandAttendeeParticipants = this.session.attendeeParticipants.filter(participant => participant.raiseHand);
            this.sendEvent(WebinarEvents.ON_RAISEHAND_CHANGE);
        }
    }

    public isTerminated(): boolean {
        return this.status === WebinarStatus.TERMINATED;
    }

    public isWaitingRoomStarted(): boolean {
        return isAfter(new Date(), subMinutes(new Date(this.webinarStartDate), 15));
    }

    public isInvitation(): boolean {
        return this.room?.bubbleConnectedUser.status === BubbleUserStatus.INVITED;
    }

    public async clone(params: WebinarCloneParams): Promise<Webinar> {
        return this.webinarService?.cloneWebinar(this, params);
    }

    /**
     * Asynchronous method to update webinar avatar on server
     * @param avatarImg - base64 encoded webinar avatar
     */
    public async updateAvatar(avatarImg?: string): Promise<Webinar> {
        return this.webinarService?.updateWebinarAvatar(this, avatarImg);
    }

    /**
     * Asynchronous method to update webinar logo on server
     * @param logoSrc - base64 encoded webinar logo
     */
    public async updateLogo(logoSrc?: string): Promise<Webinar> {
        return this.webinarService?.updateWebinarLogo(this, logoSrc);
    }

    public async acceptInvitation(): Promise<void> {
        await this.webinarService?.acceptInvitation(this);
    }

    public async join(): Promise<void> {
        await this.webinarService?.conference.joinWebinar(this);
    }

    public async addAudioVideoMedia(media: string, stream?: MediaStream, replaceBg?: boolean): Promise<void> {
        await this.webinarService?.conference.addAudioVideoMedia(this, media, stream, replaceBg);
    }

    public async addSharingMedia(stream: MediaStream): Promise<void> {
        return this.webinarService?.conference.addSharingMedia(this, stream);
    }

    public async leave(forced?: boolean, nowait?: boolean, reason?: string): Promise<void> {
        await this.webinarService?.conference.leaveWebinar(this, forced, nowait, reason);
    }

    public async joinAsRainbowAttendee(): Promise<void> {
        return this.webinarService?.participants.joinWebinarAsRainbowAttendee(this.id);
    }

    public async putLocalParticipantMixMediaOnStage(): Promise<Webinar> {
        return this.webinarService?.conference.putOnStage(this, this.webinarService.userContact.dbId, WebinarMediaType.mix, true, false);
    }

    public async putLocalParticipantAudioVideoMediaOnStage(master: boolean = false): Promise<Webinar> {
        return this.webinarService?.conference.putOnStage(this, this.webinarService.userContact.dbId, WebinarMediaType.participant, true, master);
    }

    public async putParticipantSharingMediaOnStage(participant: WebinarSessionParticipant, master: boolean = false): Promise<Webinar> {
        return this.webinarService?.conference.putOnStage(this, participant.id, WebinarMediaType.sharing, true, master);
    }

    public async removeParticipantAudioVideoMediaFromStage(participant: WebinarSessionParticipant): Promise<Webinar> {
        return this.webinarService?.conference.putOnStage(this, participant.id, WebinarMediaType.participant, false);
    }

    public async removeParticipantSharingMediaFromStage(participant: WebinarSessionParticipant): Promise<Webinar> {
        return this.webinarService?.conference.putOnStage(this, participant.id, WebinarMediaType.sharing, false);
    }

    public async shareLocalParticipantAudioConfig(audioConfig: string): Promise<Webinar> {
        return this.webinarService?.conference.shareAudioConfig(this, audioConfig);
    }

    public async subscribeToPublisher(participant: WebinarSessionParticipant, media: string): Promise<Webinar> {
        return this.webinarService?.conference.subscribeToPublisher(this, participant, media);
    }

    public async unsubscribeToPublisher(participant: WebinarSessionParticipant, media: string, disableStream?: boolean): Promise<Webinar> {
        return this.webinarService?.conference.unsubscribeToPublisher(this, participant, media, disableStream);
    }

    public async askForStaging(participant: WebinarSessionParticipant, media: string): Promise<Webinar> {
        return this.webinarService?.conference.askForStaging(this, participant, media);
    }

    public async declineStagingInvitation(participant: WebinarSessionParticipant, media: string): Promise<Webinar> {
        return this.webinarService?.conference.declineStagingInvitation(this, participant, media);
    }

    public async muteParticipant(participant: WebinarSessionParticipant, mute: boolean = true): Promise<Webinar> {
        return this.webinarService?.conference.muteParticipant(this, participant, mute);
    }

    public declineUnmuteInvitation(participant: WebinarSessionParticipant): Promise<Webinar> {
        return this.webinarService?.conference.declineUnmuteInvitation(this, participant);
    }

    public downloadRecord(): Promise<void> {
        return this.webinarService?.downloadRecord(this);
    }

    public isStageAvailable(): boolean {
        const participants = [this.session.localParticipant].concat(this.session.speakerParticipants);
        let mediasOnStage = 0;
        participants.forEach((participant: WebinarSessionParticipant) => {
            if (participant.onStage) mediasOnStage++;
            if (participant.sharingOnStage) mediasOnStage++;
        });
        return mediasOnStage <= 9;
    }

    public async allowAttendeeToTalk(userId: string): Promise<Webinar> {
        return this.webinarService?.conference.allowAttendeeToTalk(this, userId);
    }

    public async disableAttendeeToTalk(userId: string): Promise<Webinar> {
        return this.webinarService?.conference.disableAttendeeToTalk(this, userId);
    }

    public async lowerHand(userId: string): Promise<Webinar> {
        return this.webinarService?.conference.lowerHand(this, userId);
    }

    public getConnectedUserRole(): string {
        if (this.isOrganizer) return "Organizer";
        if (this.isSpeaker) return "Speaker";
        if (this.isGuestAttendee) return "GuestAttendee";
        if (this.isAttendee) return "Attendee";
        return 'Unknown';
    }

    public async sendCountdownMessage(): Promise<void> {
        try {
            const id = ConversationRB.getUniqueMessageId();
            const message = $msg({ id, from: this.xmppService.jid, to: this.room.jid, type: "groupchat" });
            message.c("asr", { xmlns: "http://jabber.org/webinar", action: "startCountDown" });
            await this.xmppService.send(message);
        } catch { /**/ }
    }

}
